// Ring map: a centre cell plus `rings` rings of 7-hex cells. Each team holds two
// corners: its HQ cell (flag on the centre hex) and its teleporter cell (where
// tagged units reappear). Team B's corners are Team A's rotated 180°.

import { Hex, distance, neighbors, mirror } from './board';
import type { Cell, Team } from './types';

// Neighbouring cell centres (hex distance 3 apart), in order around the circle.
export const CELL_DIRS: Hex[] = [
  { q: 3, r: -2 }, { q: 2, r: 1 }, { q: -1, r: 3 }, { q: -3, r: 2 }, { q: -2, r: -1 }, { q: 1, r: -3 },
];

// Screen rotation (degrees) that stands the map upright: team corners at the four diagonals.
export const UPRIGHT_ROTATION = -30 - (Math.atan2(Math.sqrt(3) * (-2 + 1.5), 4.5) * 180) / Math.PI;

const add = (a: Hex, b: Hex, k = 1): Hex => ({ q: a.q + b.q * k, r: a.r + b.r * k });
export const key = (h: Hex) => `${h.q},${h.r}`;

export function buildCells(rings: number): Cell[] {
  const centres: Hex[] = [{ q: 0, r: 0 }];
  for (let k = 1; k <= rings; k++) {
    let h = add({ q: 0, r: 0 }, CELL_DIRS[4], k);
    for (let side = 0; side < 6; side++) {
      for (let step = 0; step < k; step++) { centres.push(h); h = add(h, CELL_DIRS[side]); }
    }
  }
  const cells: Cell[] = centres.map((c, id) => ({ id, centre: c, hexes: [c, ...neighbors(c)], home: null, role: null }));
  const corner = (d: number) => cells.find(c => c.centre.q === CELL_DIRS[d].q * rings && c.centre.r === CELL_DIRS[d].r * rings)!;
  const set = (c: Cell, t: Team, role: 'hq' | 'teleporter') => { c.home = t; c.role = role; };
  set(corner(3), 'A', 'hq'); set(corner(4), 'A', 'teleporter');
  set(corner(0), 'B', 'hq'); set(corner(1), 'B', 'teleporter');
  return cells;
}

export function homeCell(cells: Cell[], t: Team, role: 'hq' | 'teleporter'): Cell {
  return cells.find(c => c.home === t && c.role === role)!;
}
export const hqOf = (cells: Cell[], t: Team) => homeCell(cells, t, 'hq').centre;

// Lookup tables built once per game (derived, so not stored in state).
export interface MapIndex {
  cellAt: Map<string, number>;
  adj: Set<number>[];
}
// Keyed by cell count: the layout is fixed per ring count, and states are
// cloned every action, so the array itself is a new object each time.
const indexCache = new Map<number, MapIndex>();
export function mapIndex(cells: Cell[]): MapIndex {
  let idx = indexCache.get(cells.length);
  if (idx) return idx;
  const cellAt = new Map<string, number>();
  for (const c of cells) for (const h of c.hexes) cellAt.set(key(h), c.id);
  const adj = cells.map(() => new Set<number>());
  for (const c of cells) for (const h of c.hexes) for (const n of neighbors(h)) {
    const o = cellAt.get(key(n));
    if (o !== undefined && o !== c.id) adj[c.id].add(o);
  }
  idx = { cellAt, adj };
  indexCache.set(cells.length, idx);
  return idx;
}

export function onMap(cells: Cell[], h: Hex): boolean {
  return mapIndex(cells).cellAt.has(key(h));
}

export function cellIdAt(cells: Cell[], h: Hex): number | undefined {
  return mapIndex(cells).cellAt.get(key(h));
}

// Starting spots by squad slot, for Team A (Team B's are mirrored). Slot 1 keeps goal on the HQ-cell hex
// that is nearest the map centre and furthest from its own teleporter. Slots 2–5 stand in the
// teleporter cell on the hexes nearest the map centre, leaving the teleporter hex free for respawns.
// Slot 4 starts facing the backyard cell between its teleporter and HQ (`toward`), slot 5 turned one
// step outward from facing the enemy HQ (`outward`), the rest facing the enemy HQ.
export interface Spot { pos: Hex; toward?: Hex; outward?: boolean }
export function deployment(cells: Cell[]): Record<Team, Spot[]> {
  const hq = homeCell(cells, 'A', 'hq');
  const tele = homeCell(cells, 'A', 'teleporter');
  const centre = { q: 0, r: 0 };
  const toCentre = (a: Hex, b: Hex) => distance(a, centre) - distance(b, centre) || a.q - b.q || a.r - b.r;
  const ring = hq.hexes.filter(h => distance(h, hq.centre) === 1);
  const near = Math.min(...ring.map(h => distance(h, centre)));
  const keeperSpot = ring.filter(h => distance(h, centre) === near)
    .sort((a, b) => distance(b, tele.centre) - distance(a, tele.centre) || toCentre(a, b))[0];
  const teleSpots = tele.hexes.filter(h => distance(h, tele.centre) === 1).sort(toCentre);
  const { adj } = mapIndex(cells);
  const backyard = cells.find(c => !c.home && adj[c.id].has(hq.id) && adj[c.id].has(tele.id));
  const a: Spot[] = [
    { pos: keeperSpot },
    { pos: teleSpots[0] }, { pos: teleSpots[1] },
    { pos: teleSpots[2], ...(backyard ? { toward: backyard.centre } : {}) },
    { pos: teleSpots[3], outward: true },
  ];
  const flip = (sp: Spot): Spot => ({ ...sp, pos: mirror(sp.pos), ...(sp.toward ? { toward: mirror(sp.toward) } : {}) });
  return { A: a, B: a.map(flip) };
}
